"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Award, Clock, Wrench, CircleCheck as CheckCircle2, Star, PhoneCall, CircleQuestionMark as HelpCircle, FileCheck2, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
const trustHighlights = [{
  icon: Award,
  title: "18+ Years Brand Experience",
  description: "Deep hands-on experience diagnosing, repairing, and installing A. O. Smith water heaters across Georgia."
}, {
  icon: ShieldCheck,
  title: "Certified Master Plumbers",
  description: "Fully licensed technicians trained in exact A. O. Smith electronic controls, burners, and elements."
}, {
  icon: FileCheck2,
  title: "OEM Parts & Warranty Safe",
  description: "We use genuine manufacturer components so your original factory warranty remains fully protected."
}, {
  icon: Clock,
  title: "24/7 Emergency Dispatch",
  description: "Equipped service trucks on call day and night throughout Jonesboro for urgent water heater breakdowns."
}];
const faqs = [{
  id: "item-1",
  question: "What does the flashing status light on my A. O. Smith gas water heater mean?",
  answer: "A. O. Smith water heaters use a smart gas control valve with a diagnostic LED indicator. A normal running unit pulses once every three seconds. If you notice two, four, or five consecutive flashes, it indicates specific conditions like a thermopile voltage issue, high tank temperature, or sensor lockout. Our technicians carry diagnostic scanners to quickly pinpoint the error code and reset or repair the control valve."
}, {
  id: "item-2",
  question: "Can Jonesboro Emergency Plumber service units covered by A. O. Smith warranty?",
  answer: "Yes. We work directly with A. O. Smith warranty requirements. We can inspect your unit, record serial and model numbers, verify active coverage for the tank and parts, and install certified OEM replacement components to ensure your existing manufacturer warranty stays valid."
}, {
  id: "item-3",
  question: "How long do A. O. Smith water heaters typically last in Jonesboro homes?",
  answer: "With Jonesboro water conditions, conventional A. O. Smith tank units typically last between 10 to 14 years when routinely maintained, while A. O. Smith tankless models can exceed 20 years. Regular anode rod inspections and annual tank flushes help prevent sediment buildup and extend the working life of the system."
}, {
  id: "item-4",
  question: "Do your plumbers carry genuine A. O. Smith replacement parts?",
  answer: "Yes. Our emergency plumbing service trucks are stocked with authentic A. O. Smith elements, thermostats, thermocouple assemblies, gas control valves, pressure relief valves, and pilot assemblies so most emergency repairs can be completed in a single visit."
}, {
  id: "item-5",
  question: "Is it better to repair my A. O. Smith heater or replace it with a high-efficiency model?",
  answer: "If your water heater is under 8 years old and the repair cost is reasonable, a component replacement is usually the best choice. If your tank is over 10 years old, showing signs of tank corrosion, or experiencing major leaks, upgrading to a modern high-efficiency A. O. Smith ProLine or tankless unit offers greater reliability and lower utility bills."
}];
const testimonials = [{
  name: "Marcus Vance",
  location: "Jonesboro, GA",
  initials: "MV",
  rating: 5,
  date: "2 weeks ago",
  unit: "A. O. Smith ProLine 50-Gal Gas",
  comment: "Our A. O. Smith water heater started blinking an error code on Sunday morning. Jonesboro Emergency Plumber arrived within 45 minutes, replaced the faulty gas valve with a factory part, and had our hot water back on before noon."
}, {
  name: "Elena Rodriguez",
  location: "Clayton County, GA",
  initials: "ER",
  rating: 5,
  date: "1 month ago",
  unit: "A. O. Smith Voltex Hybrid Electric",
  comment: "Extremely knowledgeable about the A. O. Smith hybrid series. They explained the diagnostic readout, preserved our manufacturer warranty, and were completely transparent about pricing."
}, {
  name: "Derrick Holbrook",
  location: "Jonesboro, GA",
  initials: "DH",
  rating: 5,
  date: "2 months ago",
  unit: "A. O. Smith Tankless Unit",
  comment: "Replaced our 14-year-old tank unit with an A. O. Smith tankless system. The installation was neat, code-compliant, and the endless hot water has been incredible."
}];
const certifications = ["A. O. Smith Factory-Trained", "Georgia Master Plumber License", "EPA Section 608 Certified", "Fully Insured & Bonded", "OSHA Compliant Safety Standards"];
export default function BrandFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="brandfaqandtrust" data-nav="light" className="bg-background text-foreground relative py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 font-medium">
            <ShieldCheck className="size-3.5 text-primary" />
            A. O. Smith Brand Specialist
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight sm:text-4xl">
            A. O. Smith Water Heater FAQs & Expert Credentials
          </h2>
          <p className="font-default text-muted-foreground mt-4 text-base sm:text-lg">
            Certified solutions, genuine replacement components, and fast answers from Jonesboro technicians with over 18 years of specialized brand experience.
          </p>
        </div>

        {/* Trust Badges & Experience Highlights */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {trustHighlights.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border-border bg-card shadow-sm transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="pb-3">
                    <div className="bg-primary/10 text-primary mb-3 flex size-12 items-center justify-center rounded-lg">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="font-default text-lg font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-default text-muted-foreground text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Certifications Bar */}
        <div className="border-border bg-muted/50 mt-8 rounded-xl border p-4 sm:p-6">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-5 text-primary shrink-0" />
              <span className="font-default text-sm font-semibold text-foreground">
                Verified Credentials & Compliance:
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {certifications.map((cert, i) => <span key={cert} data-index={i} className="bg-background text-foreground border-border inline-flex items-center rounded-md border px-3 py-1 text-xs font-medium shadow-2xs">
                  {cert}
                </span>)}
            </div>
          </div>
        </div>

        {/* Main Content: FAQ and Side Card */}
        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* FAQ Accordion */}
          <div className="lg:col-span-7">
            <div className="mb-6 flex items-center gap-2">
              <HelpCircle className="size-5 text-primary" />
              <h3 className="font-default text-xl font-bold tracking-tight">
                Frequently Asked A. O. Smith Questions
              </h3>
            </div>

            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              {faqs.map((faq, i) => <div key={faq.id} data-index={i}>
                  <AccordionItem value={faq.id} className="border-border py-1">
                    <AccordionTrigger className="font-default hover:text-primary text-base font-medium">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-default text-muted-foreground text-sm leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>

            <div className="border-border bg-card mt-8 rounded-lg border p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <p className="font-default text-sm font-semibold text-foreground">
                  Have an unusual A. O. Smith error code or urgent leak?
                </p>
                <p className="font-default text-muted-foreground text-xs">
                  Speak directly with a licensed water heater technician in Jonesboro.
                </p>
              </div>
              <Button asChild variant="default" size="sm" className="whitespace-nowrap">
                <Link to="/contact/">
                  Ask a Question
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Testimonials Sidebar */}
          <div className="lg:col-span-5">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="size-5 text-accent fill-accent" />
                <h3 className="font-default text-xl font-bold tracking-tight">
                  Verified Local Reviews
                </h3>
              </div>
              <Badge variant="outline" className="text-xs">
                Jonesboro, GA
              </Badge>
            </div>

            <div className="flex flex-col gap-4">
              {testimonials.map((review, i) => <Card key={review.name} data-index={i} className="border-border bg-card shadow-2xs transition-[border-color] hover:border-primary/30">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <SafeHydrate><Avatar className="size-9 bg-primary text-primary-foreground font-semibold text-xs">
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {review.initials}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <CardTitle className="font-default text-sm font-semibold text-card-foreground">
                            {review.name}
                          </CardTitle>
                          <CardDescription className="font-default text-xs text-muted-foreground">
                            {review.location}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex text-accent">
                        {Array.from({
                      length: review.rating
                    }).map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="size-3.5 fill-accent text-accent" />)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Badge variant="secondary" className="text-[11px] font-normal">
                      Unit: {review.unit}
                    </Badge>
                    <p className="font-default text-muted-foreground text-xs leading-relaxed">
                      "{review.comment}"
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            {/* Quick Emergency Action Box */}
            <div className="border-border bg-primary text-primary-foreground mt-6 rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-3">
                <div className="bg-primary-foreground/10 flex size-10 items-center justify-center rounded-full">
                  <Wrench className="size-5 text-primary-foreground" />
                </div>
                <div>
                  <h4 className="font-default text-base font-bold text-primary-foreground">
                    Need Emergency A. O. Smith Repair?
                  </h4>
                  <p className="font-default text-xs text-primary-foreground/80">
                    Fast dispatched service across Jonesboro, GA.
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <Button asChild variant="secondary" size="default" className="w-full justify-center font-semibold">
                  <Link to="/water-heater-repair/">
                    <Wrench className="size-4" />
                    Book Repair Service
                  </Link>
                </Button>
                <Button asChild variant="outline" size="default" className="w-full justify-center bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                  <Link to="/contact/">
                    <PhoneCall className="size-4" />
                    Contact Office
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}